import type { WorkflowContent, WorkflowInputDeclaration } from "../../../api/content/authoring/processContentDecoders";
import { isSubWorkflowUse, referenceOf, type Binding, type StepStart, type WorkflowRole, type WorkflowStepEntry } from "./workflowDraft";

/**
 * What changed between two versions of a Workflow, in the Workflow's own words (AU-6
 * "Changes in this draft", AU-8 "a small graph diff"): steps added, removed, re-pinned,
 * moved, given another role or start; bindings, exports and inputs' defaults added, removed or changed. The
 * graph editor lists these for its draft; the review page draws them for a change set.
 */

/** What a placement names (ADR-0043 D1): a pinned step or Workflow version, or an abstract step by its label. */
export type PlacedUse = { readonly kind: "step" | "workflow"; readonly reference: string } | { readonly kind: "abstract"; readonly label: string };

export function placedUseOf(step: WorkflowStepEntry): PlacedUse {
  if ("step" in step) return { kind: "step", reference: step.step };
  if ("workflow" in step) return { kind: "workflow", reference: step.workflow };
  return { kind: "abstract", label: step.label };
}

export type WorkflowChange =
  | { readonly kind: "stepAdded"; readonly key: string; readonly use: PlacedUse; readonly role: WorkflowRole }
  | { readonly kind: "stepRemoved"; readonly key: string }
  | { readonly kind: "stepRepinned"; readonly key: string; readonly before: string; readonly after: string }
  /** What a sub-workflow use binds to one of its Workflow's abstract steps; `null` where it binds nothing (ADR-0043 D2). */
  | { readonly kind: "definitionBound"; readonly key: string; readonly abstractStep: string; readonly before: string | null; readonly after: string | null }
  | { readonly kind: "roleChanged"; readonly key: string; readonly before: WorkflowRole; readonly after: WorkflowRole }
  | { readonly kind: "startChanged"; readonly key: string; readonly before: StepStart; readonly after: StepStart }
  | { readonly kind: "stepMoved"; readonly key: string }
  | { readonly kind: "bindingAdded"; readonly key: string; readonly slot: string; readonly from: Binding }
  | { readonly kind: "bindingRemoved"; readonly key: string; readonly slot: string; readonly from: Binding }
  | { readonly kind: "bindingChanged"; readonly key: string; readonly slot: string; readonly before: Binding; readonly after: Binding }
  | { readonly kind: "exportAdded"; readonly name: string; readonly from: Binding }
  | { readonly kind: "exportRemoved"; readonly name: string; readonly from: Binding }
  | { readonly kind: "exportChanged"; readonly name: string; readonly before: Binding; readonly after: Binding }
  /** The Workflow's own name or description, in a language (WD-1+3 D-9). */
  | { readonly kind: "textsChanged" }
  /** The Workflow default of a step input, by its destination (`<key>.<input>`, WD-1+3 S4-G1). */
  | { readonly kind: "defaultAdded"; readonly destination: string; readonly value: unknown }
  | { readonly kind: "defaultChanged"; readonly destination: string; readonly before: unknown; readonly after: unknown }
  | { readonly kind: "defaultRemoved"; readonly destination: string; readonly value: unknown };

export function workflowChanges(before: WorkflowContent, after: WorkflowContent): readonly WorkflowChange[] {
  const changes: WorkflowChange[] = [];
  if (before.name !== after.name || before.description !== after.description || JSON.stringify(before.translations ?? null) !== JSON.stringify(after.translations ?? null)) {
    changes.push({ kind: "textsChanged" });
  }
  const beforeByKey = new Map(before.steps.map((step) => [step.key, step]));
  const afterKeys = new Set(after.steps.map((step) => step.key));
  const moved = movedKeys(before, after);
  for (const step of after.steps) {
    const old = beforeByKey.get(step.key);
    if (old === undefined) {
      changes.push({ kind: "stepAdded", key: step.key, use: placedUseOf(step), role: step.role });
    } else {
      changes.push(...stepChanges(old, step), ...(moved.has(step.key) ? [{ kind: "stepMoved" as const, key: step.key }] : []));
    }
    changes.push(...bindingChanges(step.key, old === undefined ? {} : bindingsOf(old), bindingsOf(step)));
  }
  for (const step of before.steps) {
    if (!afterKeys.has(step.key)) {
      changes.push({ kind: "stepRemoved", key: step.key });
    }
  }
  for (const [name, from] of Object.entries(after.exports)) {
    const old = before.exports[name];
    if (old === undefined) {
      changes.push({ kind: "exportAdded", name, from });
    } else if (!sameBinding(old, from)) {
      changes.push({ kind: "exportChanged", name, before: old, after: from });
    }
  }
  for (const [name, from] of Object.entries(before.exports)) {
    if (!(name in after.exports)) {
      changes.push({ kind: "exportRemoved", name, from });
    }
  }
  changes.push(...defaultChanges(before.declared_inputs, after.declared_inputs));
  return changes;
}

/** Defaults by destination: storing the inputs a first default needs changes no other input (S4-G1). */
function defaultChanges(before: readonly WorkflowInputDeclaration[] | null | undefined, after: readonly WorkflowInputDeclaration[] | null | undefined): WorkflowChange[] {
  const defaults = (inputs: readonly WorkflowInputDeclaration[] | null | undefined) =>
    new Map((inputs ?? []).flatMap((input) => (input.default === null || input.default === undefined ? [] : [[input.destination, input.default] as const])));
  const old = defaults(before);
  const now = defaults(after);
  const changes: WorkflowChange[] = [];
  for (const [destination, value] of now) {
    const earlier = old.get(destination);
    if (earlier === undefined) {
      changes.push({ kind: "defaultAdded", destination, value });
    } else if (JSON.stringify(earlier) !== JSON.stringify(value)) {
      changes.push({ kind: "defaultChanged", destination, before: earlier, after: value });
    }
  }
  for (const [destination, value] of old) {
    if (!now.has(destination)) {
      changes.push({ kind: "defaultRemoved", destination, value });
    }
  }
  return changes;
}

function stepChanges(before: WorkflowStepEntry, after: WorkflowStepEntry): WorkflowChange[] {
  const changes: WorkflowChange[] = [];
  const [pinnedBefore, pinnedAfter] = [referenceOf(before), referenceOf(after)];
  if (pinnedBefore !== null && pinnedAfter !== null && pinnedBefore !== pinnedAfter) {
    changes.push({ kind: "stepRepinned", key: after.key, before: pinnedBefore, after: pinnedAfter });
  }
  changes.push(...definitionChanges(after.key, boundOf(before), boundOf(after)));
  if (before.role !== after.role) {
    changes.push({ kind: "roleChanged", key: after.key, before: before.role, after: after.role });
  }
  if (!isSubWorkflowUse(before) && !isSubWorkflowUse(after) && before.start !== after.start) {
    changes.push({ kind: "startChanged", key: after.key, before: before.start, after: after.start });
  }
  return changes;
}

function definitionChanges(key: string, before: Readonly<Record<string, string>>, after: Readonly<Record<string, string>>): WorkflowChange[] {
  const abstractSteps = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return abstractSteps.flatMap((abstractStep) => {
    const [old, now] = [before[abstractStep] ?? null, after[abstractStep] ?? null];
    return old === now ? [] : [{ kind: "definitionBound" as const, key, abstractStep, before: old, after: now }];
  });
}

/** The version a sub-workflow use binds to each abstract step, by its key. */
function boundOf(step: WorkflowStepEntry): Readonly<Record<string, string>> {
  return isSubWorkflowUse(step) ? Object.fromEntries(Object.entries(step.definitions ?? {}).map(([abstractStep, binding]) => [abstractStep, binding.definition])) : {};
}

function bindingChanges(key: string, before: Readonly<Record<string, Binding>>, after: Readonly<Record<string, Binding>>): WorkflowChange[] {
  const changes: WorkflowChange[] = [];
  for (const [slot, from] of Object.entries(after)) {
    const old = before[slot];
    if (old === undefined) {
      changes.push({ kind: "bindingAdded", key, slot, from });
    } else if (!sameBinding(old, from)) {
      changes.push({ kind: "bindingChanged", key, slot, before: old, after: from });
    }
  }
  for (const [slot, from] of Object.entries(before)) {
    if (!(slot in after)) {
      changes.push({ kind: "bindingRemoved", key, slot, from });
    }
  }
  return changes;
}

/** Steps kept in both versions whose place among the kept steps changed. */
function movedKeys(before: WorkflowContent, after: WorkflowContent): ReadonlySet<string> {
  const afterKeys = new Set(after.steps.map((step) => step.key));
  const beforeKeys = new Set(before.steps.map((step) => step.key));
  const keptBefore = before.steps.map((step) => step.key).filter((key) => afterKeys.has(key));
  const keptAfter = after.steps.map((step) => step.key).filter((key) => beforeKeys.has(key));
  return new Set(keptAfter.filter((key, index) => keptBefore[index] !== key));
}

/** The wires a placement writes: a step's or abstract step's slots, a sub-workflow use's declared inputs by name (ADR-0043 D3). */
function bindingsOf(step: WorkflowStepEntry): Readonly<Record<string, Binding>> {
  return step.input_bindings ?? {};
}

function sameBinding(left: Binding, right: Binding): boolean {
  return left.from_step === right.from_step && left.part === right.part;
}

export type DiffStatus = "added" | "removed" | "changed" | "same";

export interface GraphDiffNode {
  readonly key: string;
  readonly use: PlacedUse;
  readonly role: WorkflowRole;
  readonly status: DiffStatus;
}

export interface GraphDiffEdge {
  readonly key: string;
  readonly slot: string;
  readonly from: Binding;
  readonly status: Exclude<DiffStatus, "changed">;
}

/**
 * The small graph diff of the review (AU-8): every step of either version in pipeline
 * order (a removed step stays where it was), marked added, removed, changed or the same,
 * and every binding, marked added, removed or the same.
 */
export function workflowGraphDiff(before: WorkflowContent, after: WorkflowContent): { readonly nodes: readonly GraphDiffNode[]; readonly edges: readonly GraphDiffEdge[] } {
  const changes = workflowChanges(before, after);
  const changedKeys = new Set(changes.flatMap((change) => (change.kind === "stepRepinned" || change.kind === "definitionBound" || change.kind === "roleChanged" || change.kind === "startChanged" || change.kind === "stepMoved" ? [change.key] : [])));
  const afterKeys = new Set(after.steps.map((step) => step.key));
  const beforeKeys = new Set(before.steps.map((step) => step.key));
  const node = (step: WorkflowStepEntry, status: DiffStatus): GraphDiffNode => ({ key: step.key, use: placedUseOf(step), role: step.role, status });

  const nodes: GraphDiffNode[] = after.steps.map((step) => node(step, !beforeKeys.has(step.key) ? "added" : changedKeys.has(step.key) ? "changed" : "same"));
  before.steps.forEach((step, index) => {
    if (afterKeys.has(step.key)) {
      return;
    }
    // After the step that came before it, if that one is still there.
    const previous = before.steps.slice(0, index).reverse().find((candidate) => afterKeys.has(candidate.key) || nodes.some((shown) => shown.key === candidate.key));
    const at = previous === undefined ? 0 : nodes.findIndex((shown) => shown.key === previous.key) + 1;
    nodes.splice(at, 0, node(step, "removed"));
  });

  const edges: GraphDiffEdge[] = [];
  const beforeByKey = new Map(before.steps.map((step) => [step.key, bindingsOf(step)]));
  for (const step of after.steps) {
    const old = beforeByKey.get(step.key) ?? {};
    for (const [slot, from] of Object.entries(bindingsOf(step))) {
      const previous = old[slot];
      edges.push({ key: step.key, slot, from, status: previous !== undefined && sameBinding(previous, from) ? "same" : "added" });
      if (previous !== undefined && !sameBinding(previous, from)) {
        edges.push({ key: step.key, slot, from: previous, status: "removed" });
      }
    }
  }
  for (const step of before.steps) {
    const now = afterKeys.has(step.key) ? bindingsOf(after.steps.find((candidate) => candidate.key === step.key) ?? step) : {};
    for (const [slot, from] of Object.entries(bindingsOf(step))) {
      if (!(slot in now)) {
        edges.push({ key: step.key, slot, from, status: "removed" });
      }
    }
  }
  return { nodes, edges };
}
